<template>
  <div class="node-pools-table-summary" aid="node-pools-summary">
    <div class="icon-wrapper">
      <runai-svg-icon :name="iconName" size="22" />
    </div>
    <div class="node-pools-table-summary-row">
      <div v-for="(label, index) in labels" :key="label.name" :class="getLabelClass(index)" :aid="label.aid">
        {{ label.value }}
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
//cmps
import { RunaiSvgIcon } from "@/components/common/runai-svg-icon";

//models
import { EQuotaEntity, EResourceType } from "@/models/resource.model";
//services
import { resourceUtil } from "@/utils/resource.util";

//stores
import { useSettingStore } from "@/stores/setting.store";
import type { Resources } from "@/swagger-models/org-unit-service-client";
import { EQuotaTab } from "@/models/org-unit.model";

enum EHeaderLabel {
  ProjectTotal = "Project total",
  DepartmentTotal = "Department total",
}

enum EHeaderName {
  ProjectTotal = "ProjectTotal",
  DepartmentTotal = "DepartmentTotal",
  GpuQuota = "GpuQuota",
  CpuQuota = "CpuQuota",
  CpuMemoryQuota = "CpuMemoryQuota",
  Limit = "Limit",
}

interface HeaderConfig {
  name: EHeaderName;
  value?: string | number;
  display: boolean;
  aid?: string;
}

export default defineComponent({
  components: {
    RunaiSvgIcon,
  },
  inject: ["entity"],
  props: {
    resources: {
      type: Array as PropType<Array<Resources>>,
      required: true,
    },
    isOverQuotaPriorityEnabled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    selectedTab: {
      type: String as PropType<EQuotaTab>,
      required: true,
    },
  },
  data() {
    return {
      settingStore: useSettingStore(),
    };
  },
  computed: {
    isProjectEntity(): boolean {
      return this.entity === EQuotaEntity.project;
    },
    EQuotaEntity(): typeof EQuotaEntity {
      return EQuotaEntity;
    },
    totalGpus(): string | number {
      return resourceUtil.getResourcesDisplayValue(this.resources, EResourceType.GPU);
    },
    totalCpus(): number | string {
      return resourceUtil.getResourcesDisplayValue(this.resources, EResourceType.CPU);
    },
    totalMemory(): number | string {
      return resourceUtil.getResourcesDisplayValue(this.resources, EResourceType.MEMORY);
    },
    totalLimit(): string | number {
      return resourceUtil.getResourcesDisplayValue(this.resources, EResourceType.GPU_LIMIT);
    },
    isCpuEnabled(): boolean {
      return this.settingStore.isCPUResourcesQuotaEnabled;
    },
    header(): string {
      return this.isProjectEntity ? "Project total" : "Department total";
    },
    iconName(): string {
      return this.isProjectEntity ? "project-icon" : "departments-icon";
    },
    isSelectedTabQuota(): boolean {
      return this.selectedTab === EQuotaTab.Quota;
    },
    headerMap(): Record<EQuotaTab, HeaderConfig[]> {
      return {
        [EQuotaTab.Quota]: [
          {
            name: EHeaderName.ProjectTotal,
            value: EHeaderLabel.ProjectTotal,
            display: this.isProjectEntity,
            aid: "summary-header",
          },
          {
            name: EHeaderName.DepartmentTotal,
            value: EHeaderLabel.DepartmentTotal,
            display: !this.isProjectEntity,
            aid: "summary-header",
          },
          {
            name: EHeaderName.GpuQuota,
            value: this.totalGpus,
            display: true,
            aid: "summary-total-gpu",
          },
          {
            name: EHeaderName.CpuQuota,
            value: this.totalCpus,
            display: this.isCpuEnabled,
            aid: "summary-total-cpu",
          },
          {
            name: EHeaderName.CpuMemoryQuota,
            value: this.totalMemory,
            display: this.isCpuEnabled,
            aid: "summary-total-memory",
          },
        ],
        [EQuotaTab.Scheduling]: [
          {
            name: EHeaderName.ProjectTotal,
            value: EHeaderLabel.ProjectTotal,
            display: this.isProjectEntity,
          },
          {
            name: EHeaderName.DepartmentTotal,
            value: EHeaderLabel.DepartmentTotal,
            display: !this.isProjectEntity,
          },
          {
            name: EHeaderName.Limit,
            value: this.totalLimit,
            display: true,
          },
        ],
      };
    },
    labels(): HeaderConfig[] {
      return this.headerMap[this.selectedTab].filter((header) => header.display);
    },
  },
  methods: {
    getLabelClass(index: number): string {
      if (!this.isSelectedTabQuota && index !== 0 && this.isProjectEntity) {
        return "text-center";
      }
      return this.isSelectedTabQuota && index !== 0 ? "text-center" : "";
    },
  },
});
</script>

<style lang="scss" scoped>
.node-pools-table-summary {
  height: 50px;
  display: flex;
  align-items: center;
  width: 100%;

  .icon-wrapper {
    position: relative;
    background-color: $body-background-color;
    border-radius: 4px;
    width: 57px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    margin-right: 9px;
  }

  .node-pools-table-summary-row {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    background-color: $body-background-color;
    padding: 0 20px;
    font-size: 16px;
    font-weight: 500;
    gap: 8px;
  }
  .node-pools-table-summary-row > *:first-child {
    flex-grow: 1;
  }
  .node-pools-table-summary-row > *:not(:first-child) {
    min-width: 100px;
  }
  .node-pools-table-summary-row > *:nth-child(n + 2) {
    flex-shrink: 0;
  }
}
</style>
